/**
 * Google Ads conversion tracking functions
 *
 * Note: This module provides convenience functions for subscription tracking.
 * For general conversion tracking, use @/lib/analytics/conversion/google instead.
 */

import type { ConversionData, EnhancedConversionData } from './types'
import { hashEmail, hashPhone } from './utils'
import { GOOGLE_ADS_TAG_ID } from '@/lib/constant'

// Re-export from unified conversion system for convenience
export { sendGoogleAdsConversion } from '@/lib/analytics/conversion/google'

/**
 * Send Google Ads conversion event
 * This should be called on the client side (browser) after payment success
 *
 * @param conversionLabel - The conversion label from Google Ads (e.g., "AbC123XyZ")
 * @param data - Conversion data including transaction_id, value, currency
 * @param enhancedData - Optional user data for enhanced conversions (will be hashed)
 */
export async function trackGoogleAdsConversion(
  conversionLabel: string,
  data: ConversionData,
  enhancedData?: EnhancedConversionData,
): Promise<void> {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('gtag not available, skipping Google Ads conversion tracking')
    return
  }

  if (!conversionLabel) {
    console.warn(
      'No conversion label provided, skipping Google Ads conversion tracking',
    )
    return
  }

  try {
    // Set enhanced conversion data if provided
    if (enhancedData) {
      const userData: Record<string, any> = {}

      if (enhancedData.email) {
        userData.email = await hashEmail(enhancedData.email)
      }

      if (enhancedData.phone_number) {
        userData.phone_number = await hashPhone(enhancedData.phone_number)
      }

      if (enhancedData.address) {
        userData.address = {
          first_name: enhancedData.address.first_name?.toLowerCase().trim(),
          last_name: enhancedData.address.last_name?.toLowerCase().trim(),
          street: enhancedData.address.street?.toLowerCase().trim(),
          city: enhancedData.address.city?.toLowerCase().trim(),
          region: enhancedData.address.region?.toLowerCase().trim(),
          postal_code: enhancedData.address.postal_code?.toLowerCase().trim(),
          country: enhancedData.address.country?.toLowerCase().trim(),
        }
      }

      // Set user data for enhanced conversions
      window.gtag('set', 'user_data', userData)
    }

    // Send conversion event
    // Use GOOGLE_ADS_TAG_ID (AW ID) for Google Ads conversion tracking
    // Google Ads conversions require AW ID format: "AW-{ACCOUNT_ID}/{LABEL}"
    window.gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_TAG_ID}/${conversionLabel}`,
      value: data.value,
      currency: data.currency,
      transaction_id: data.transaction_id,
    })

    console.log('Google Ads conversion tracked:', {
      conversionLabel,
      transaction_id: data.transaction_id,
      value: data.value,
      currency: data.currency,
      enhanced: !!enhancedData,
    })
  } catch (error) {
    console.error('Failed to track Google Ads conversion:', error)
  }
}

/**
 * Track subscription purchase conversion
 * Convenience wrapper for subscription purchases
 */
export async function trackSubscriptionConversion(
  conversionLabel: string,
  transactionId: string,
  amount: number,
  userEmail?: string,
): Promise<void> {
  const conversionData: ConversionData = {
    transaction_id: transactionId,
    value: amount,
    currency: 'USD',
  }

  const enhancedData: EnhancedConversionData | undefined = userEmail
    ? { email: userEmail }
    : undefined

  await trackGoogleAdsConversion(conversionLabel, conversionData, enhancedData)
}
